module.exports = function (Colors, EventEmitter, WaveformPoints, WaveformSegments, Player, ViewController, TimeController, ZoomController, WaveformBuilder, mixins, Utils, KeyboardHandler, CueEmitter) {
    'use strict';
    function buildUi(container) {
        return {
            player: container.querySelector('.waveform'),
            zoomview: container.querySelector('.zoom-container'),
            overview: container.querySelector('.overview-container')
        };
    }
    function Peaks() {
        EventEmitter.call(this, { wildcard: true });
        this.options = {
            zoomLevels: [
                512,
                1024,
                2048,
                4096
            ],
            dataUri: null,
            dataUriDefaultFormat: 'json',
            withCredentials: false,
            logger: null,
            deprecationLogger: console.log.bind(console),
            keyboard: false,
            nudgeIncrement: 1,
            inMarkerColor: Colors.gray,
            outMarkerColor: Colors.gray,
            zoomWaveformColor: 'rgba(0, 225, 128, 1)',
            overviewWaveformColor: 'rgba(0,0,0,0.2)',
            overviewHighlightColor: 'grey',
            overviewHighlightOffset: 11,
            randomizeSegmentColor: true,
            height: 200,
            segmentColor: Colors.orange,
            playheadColor: Colors.black,
            playheadTextColor: Colors.gray,
            showPlayheadTime: false,
            axisGridlineColor: '#ccc',
            axisLabelColor: Colors.gray,
            template: [
                '<div class="waveform">',
                '<div class="zoom-container"></div>',
                '<div class="overview-container"></div>',
                '</div>'
            ].join(''),
            pointMarkerColor: Colors.teal,
            pointDblClickHandler: null,
            pointDragEndHandler: null,
            webAudio: null,
            zoomAdapter: 'static',
            emitCueEvents: false,
            createSegmentMarker: mixins.createSegmentMarker,
            createSegmentLabel: mixins.createSegmentLabel,
            createPointMarker: mixins.createPointMarker
        };
        this.logger = console.error.bind(console);
        return this;
    }
    Peaks.prototype = Object.create(EventEmitter.prototype);
    Peaks.init = function (opts, callback) {
        var instance = new Peaks();
        opts = opts || {};
        instance._setOptions(opts);
        var containers = null;
        if (typeof instance.options.template === 'string') {
            opts.container.innerHTML = instance.options.template;
            containers = buildUi(instance.options.container);
        } else if (Utils.isHTMLElement(instance.options.template)) {
            this.container.appendChild(instance.options.template);
            containers = buildUi(instance.options.container);
        } else if (instance.options.containers) {
            containers = instance.options.containers;
        } else {
            throw new TypeError('Peaks.init(): The template option must be a valid HTML string or a DOM object');
        }
        var zoomviewContainer = containers.zoomview || containers.zoom;
        if (!Utils.isHTMLElement(zoomviewContainer) && !Utils.isHTMLElement(containers.overview)) {
            throw new TypeError('Peaks.init(): The containers.zoomview and/or containers.overview options must be valid HTML elements');
        }
        if (zoomviewContainer && zoomviewContainer.clientWidth <= 0) {
            throw new TypeError('Peaks.init(): Please ensure that the zoomview container is visible and has non-zero width');
        }
        if (containers.overview && containers.overview.clientWidth <= 0) {
            throw new TypeError('Peaks.init(): Please ensure that the overview container is visible and has non-zero width');
        }
        if (instance.options.keyboard) {
            instance.keyboardHandler = new KeyboardHandler(instance);
        }
        instance.player = new Player(instance, instance.options.mediaElement);
        instance.segments = new WaveformSegments(instance);
        instance.points = new WaveformPoints(instance);
        instance.zoom = new ZoomController(instance, instance.options.zoomLevels);
        instance.time = new TimeController(instance);
        instance.views = new ViewController(instance);
        var waveformBuilder = new WaveformBuilder(instance);
        waveformBuilder.init(instance.options, function (err, waveformData) {
            if (err) {
                if (callback) {
                    callback(err);
                }
                return;
            }
            instance._waveformData = waveformData;
            if (containers.overview) {
                instance.views.createOverview(containers.overview);
            }
            if (zoomviewContainer) {
                instance.views.createZoomview(zoomviewContainer);
            }
            instance._addWindowResizeHandler();
            if (instance.options.segments) {
                instance.segments.add(instance.options.segments);
            }
            if (instance.options.points) {
                instance.points.add(instance.options.points);
            }
            if (instance.options.emitCueEvents) {
                instance._cueEmitter = new CueEmitter(instance);
            }
            instance.emit('segments.ready');
            instance.emit('peaks.ready');
            if (callback) {
                callback(null, instance);
            }
        });
        return instance;
    };
    Peaks.prototype._setOptions = function (opts) {
        opts.deprecationLogger = opts.deprecationLogger || console.log.bind(console);
        if (opts.audioElement) {
            opts.mediaElement = opts.audioElement;
            opts.deprecationLogger('Peaks.init(): The audioElement option is deprecated, please use mediaElement instead');
        }
        if (opts.overviewHighlightRectangleColor) {
            opts.overviewHighlightColor = opts.overviewHighlightRectangleColor;
            opts.deprecationLogger('overviewHighlightRectangleColor has been renamed overviewHighlightColor');
        }
        if (!opts.mediaElement) {
            throw new Error('Peaks.init(): Missing mediaElement option');
        }
        if (!(opts.mediaElement instanceof HTMLMediaElement)) {
            throw new TypeError('Peaks.init(): The mediaElement option should be an HTMLMediaElement');
        }
        if (!opts.container && !opts.containers) {
            throw new Error('Peaks.init(): Please specify either a container or containers option');
        } else if (Boolean(opts.container) === Boolean(opts.containers)) {
            throw new Error('Peaks.init(): Please specify either a container or containers option, but not both');
        }
        if (opts.template && opts.containers) {
            throw new Error('Peaks.init(): Please specify either a template or a containers option, but not both');
        }
        if (opts.containers) {
            opts.template = null;
        }
        if (opts.logger && !Utils.isFunction(opts.logger)) {
            throw new TypeError('Peaks.init(): The logger option should be a function');
        }
        if (opts.segments && !Array.isArray(opts.segments)) {
            throw new TypeError('Peaks.init(): options.segments must be an array of segment objects');
        }
        if (opts.points && !Array.isArray(opts.points)) {
            throw new TypeError('Peaks.init(): options.points must be an array of point objects');
        }
        Utils.extend(this.options, opts);
        if (!Array.isArray(this.options.zoomLevels)) {
            throw new TypeError('Peaks.init(): The zoomLevels option should be an array');
        } else if (this.options.zoomLevels.length === 0) {
            throw new Error('Peaks.init(): The zoomLevels array must not be empty');
        } else {
            if (!Utils.isInAscendingOrder(this.options.zoomLevels)) {
                throw new Error('Peaks.init(): The zoomLevels array must be sorted in ascending order');
            }
        }
        if (this.options.pointDblClickHandler) {
            opts.deprecationLogger('Peaks.init(): The pointDblClickHandler option is deprecated, please use the points.dblclick event instead');
            this.on('points.dblclick', this.options.pointDblClickHandler);
        }
        if (this.options.pointDragEndHandler) {
            opts.deprecationLogger('Peaks.init(): The pointDragEndHandler option is deprecated, please use the points.dragend event instead');
            this.on('points.dragend', this.options.pointDragEndHandler);
        }
        if (opts.logger) {
            this.logger = opts.logger;
        }
    };
    Peaks.prototype.setSource = function (options, callback) {
        var self = this;
        if (!options.mediaUrl) {
            callback(new Error('peaks.setSource(): options must contain a mediaUrl'));
            return;
        }
        function reset() {
            self.removeAllListeners('player_canplay');
            self.removeAllListeners('player_error');
        }
        function playerErrorHandler(err) {
            reset();
            callback(err);
        }
        function playerCanPlayHandler() {
            reset();
            if (!options.zoomLevels) {
                options.zoomLevels = self.options.zoomLevels;
            }
            var waveformBuilder = new WaveformBuilder(self);
            waveformBuilder.init(options, function (err, waveformData) {
                if (err) {
                    callback(err);
                    return;
                }
                self._waveformData = waveformData;
                [
                    'overview',
                    'zoomview'
                ].forEach(function (viewName) {
                    var view = self.views.getView(viewName);
                    if (view) {
                        view.setWaveformData(waveformData);
                    }
                });
                self.zoom.setZoomLevels(options.zoomLevels);
                callback();
            });
        }
        self.once('player_canplay', playerCanPlayHandler);
        self.once('player_error', playerErrorHandler);
        self.player.setSource(options.mediaUrl);
    };
    Peaks.prototype.getWaveformData = function () {
        return this._waveformData;
    };
    Peaks.prototype._addWindowResizeHandler = function () {
        this._onResize = this._onResize.bind(this);
        window.addEventListener('resize', this._onResize);
    };
    Peaks.prototype._onResize = function () {
        this.emit('window_resize');
    };
    Peaks.prototype._removeWindowResizeHandler = function () {
        window.removeEventListener('resize', this._onResize);
    };
    Peaks.prototype.destroy = function () {
        this._removeWindowResizeHandler();
        if (this.views) {
            this.views.destroy();
        }
        if (this.player) {
            this.player.destroy();
        }
        if (this._cueEmitter) {
            this._cueEmitter.destroy();
        }
    };
    return Peaks;
}(require('colors.css'), require('EventEmitter'), require('peaks/markers/waveform.points'), require('peaks/markers/waveform.segments'), require('peaks/player/player'), require('peaks/views/view-controller'), require('peaks/views/waveform.timecontroller'), require('peaks/views/waveform.zoomcontroller'), require('peaks/waveform/waveform-builder'), require('peaks/waveform/waveform.mixins'), require('peaks/waveform/waveform.utils'), require('peaks/player/player.keyboard'), require('peaks/cues/cue-emitter'));